import { BottomSheet, Input, Text, useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Button, DialogContent, Stack, ThemeProvider } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { Controller, useForm } from "react-hook-form";

export interface NegotiationBlockButtonProps {
  isDisabled: boolean;
  hourlyPayForShiftBlock: number;
  shiftBlockId: string;
  workplaceId: string;
  workerId: string;
  workplaceName: string;
  workerName: string;
}

interface NegotiateBlockFormValues {
  requestedPay: string;
  requestedSchedule: string;
  reason: string;
}

export function NegotiationBlockButton(props: NegotiationBlockButtonProps) {
  const {
    isDisabled,
    hourlyPayForShiftBlock,
    shiftBlockId,
    workplaceId,
    workerId,
    workplaceName,
    workerName,
  } = props;
  const negotiateBlockState = useModalState();
  const { showErrorToast, showSuccessToast } = useToast();

  const { control, handleSubmit, reset } = useForm<NegotiateBlockFormValues>({
    defaultValues: {
      requestedPay: "",
      requestedSchedule: "",
      reason: "",
    },
  });

  const onSubmit = (data: NegotiateBlockFormValues) => {
    const { requestedPay, requestedSchedule, reason } = data;

    if (requestedPay.trim() === "" && requestedSchedule.trim() === "") {
      showErrorToast("Proposal not sent - you did not provide a different rate or schedule");
      return;
    }

    logEvent(APP_V2_USER_EVENTS.NEGOTIATE_BLOCK_PROPOSAL_SUBMITTED, {
      shiftBlockId,
      requestedPay,
      requestedSchedule,
      reason,
      workplaceId,
      workerId,
      workplaceName,
      workerName,
    });
    negotiateBlockState.closeModal();
    showSuccessToast("Proposal sent");
    reset();
  };

  return (
    <>
      <Button
        fullWidth
        variant="outlined"
        color="primary"
        size="medium"
        disabled={isDisabled}
        sx={{
          mt: 1,
          backgroundColor: "white",
        }}
        onClick={() => {
          negotiateBlockState.openModal();
        }}
      >
        Negotiate pay or schedule
      </Button>
      <ThemeProvider theme={getTheme()}>
        <BottomSheet
          canDismiss
          modalState={negotiateBlockState}
          footer={
            <DialogFooter
              orientation="vertical"
              onClose={() => {
                negotiateBlockState.closeModal();
              }}
            >
              <Button fullWidth size="large" variant="contained" onClick={handleSubmit(onSubmit)}>
                Submit Proposal
              </Button>
            </DialogFooter>
          }
        >
          <DialogContent sx={{ paddingX: 5 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack direction="column" spacing={6} sx={{ marginTop: 5 }}>
                <Stack direction="column" spacing={4}>
                  <Text variant="body1">Request higher pay / hr</Text>
                  <Controller
                    name="requestedPay"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder={`${formatDollarsAsUsd(hourlyPayForShiftBlock)} (current)`}
                        sx={{ borderRadius: 4 }}
                      />
                    )}
                  />
                </Stack>
                <Stack direction="column" spacing={4}>
                  <Text variant="body1">Dates and shift schedule</Text>
                  <Controller
                    name="requestedSchedule"
                    control={control}
                    render={({ field }) => (
                      <Input {...field} placeholder="Same schedule" sx={{ borderRadius: 4 }} />
                    )}
                  />
                </Stack>
                <Stack direction="column" spacing={4}>
                  <Text variant="body1">Reason (optional)</Text>
                  <Controller
                    name="reason"
                    control={control}
                    render={({ field }) => <Input {...field} sx={{ borderRadius: 4 }} />}
                  />
                </Stack>
              </Stack>
              {/* Hidden submit button for form submission via Enter key */}
              <button type="submit" style={{ display: "none" }} />
            </form>
          </DialogContent>
        </BottomSheet>
      </ThemeProvider>
    </>
  );
}
